// Copyright (c) 2021. Sendanor <info@sendanor.fi>. All rights reserved.

import FormModel from "../nor/ui/types/FormModel";
import LogService from "../nor/ts/LogService";
import Observer, {ObserverCallback, ObserverDestructor} from "../nor/ts/Observer";
import WindowEventService, {WindowEventServiceDestructor} from "../nor/ui/services/WindowEventService";
import {JsonObject} from "../nor/ts/Json";
import {FormMessageType, isFormMessageType} from "./types/FormMessageType";
import {isFormSetValueMessageDTO} from "./types/FormSetValueMessageDTO";
import {isFormSetModelMessageDTO} from "./types/FormSetModelMessageDTO";
import {FormValue} from "../nor/ui/types/FormValue";
import {isFormSetSubmitMessageDTO} from "./types/FormSetSubmitMessageDTO";
import {isFormSetCancelMessageDTO} from "./types/FormSetCancelMessageDTO";
import {isFormUnsetCancelMessageDTO} from "./types/FormUnsetCancelMessageDTO";
import {isFormUnsetSubmitMessageDTO} from "./types/FormUnsetSubmitMessageDTO";
import {FormWindowEventServiceEvent} from "./types/FormWindowEventServiceEvent";
import {FormSubmitMessageDTO} from "./types/FormSubmitMessageDTO";
import {FormCancelMessageDTO} from "./types/FormCancelMessageDTO";
import WindowService from "../nor/ui/services/WindowService";

const LOG = LogService.createLogger('WindowEventService');

export function isWindowMessageType (value: any) : value is FormMessageType {
    switch (value) {

        case FormMessageType.SET_MODEL:
        case FormMessageType.SET_VALUE:
            return true;

        default:
            return false;
    }
}

export interface FormWindowEventServiceOnSetModelCallback {
    (name : FormWindowEventServiceEvent.SET_MODEL, data: FormModel, origin: string) : void;
}

export interface FormWindowEventServiceOnSetValueCallback {
    (name : FormWindowEventServiceEvent.SET_VALUE, data: FormValue, origin: string) : void;
}

export interface FormWindowEventServiceOnSetSubmitCallback {
    (name : FormWindowEventServiceEvent.SET_SUBMIT, origin: string) : void;
}

export interface FormWindowEventServiceOnSetCancelCallback {
    (name : FormWindowEventServiceEvent.SET_CANCEL, origin: string) : void;
}

export interface FormWindowEventServiceOnUnsetSubmitCallback {
    (name : FormWindowEventServiceEvent.UNSET_SUBMIT, origin: string) : void;
}

export interface FormWindowEventServiceOnUnsetCancelCallback {
    (name : FormWindowEventServiceEvent.UNSET_CANCEL, origin: string) : void;
}

export type FormWindowEventServiceDestructor = ObserverDestructor;

export class FormWindowEventService {

    private static _observer        : Observer<FormWindowEventServiceEvent> = new Observer<FormWindowEventServiceEvent>("FormWindowEventService");
    private static _messageListener : WindowEventServiceDestructor | undefined;

    public static Event = FormWindowEventServiceEvent;

    public static on ( name : FormWindowEventServiceEvent.SET_MODEL    , callback: FormWindowEventServiceOnSetModelCallback)    : FormWindowEventServiceDestructor;
    public static on ( name : FormWindowEventServiceEvent.SET_VALUE    , callback: FormWindowEventServiceOnSetValueCallback)    : FormWindowEventServiceDestructor;
    public static on ( name : FormWindowEventServiceEvent.SET_SUBMIT   , callback: FormWindowEventServiceOnSetSubmitCallback)   : FormWindowEventServiceDestructor;
    public static on ( name : FormWindowEventServiceEvent.SET_CANCEL   , callback: FormWindowEventServiceOnSetCancelCallback)   : FormWindowEventServiceDestructor;
    public static on ( name : FormWindowEventServiceEvent.UNSET_SUBMIT , callback: FormWindowEventServiceOnUnsetSubmitCallback) : FormWindowEventServiceDestructor;
    public static on ( name : FormWindowEventServiceEvent.UNSET_CANCEL , callback: FormWindowEventServiceOnUnsetCancelCallback) : FormWindowEventServiceDestructor;

    public static on (
        name     : (
              FormWindowEventServiceEvent.SET_MODEL
            | FormWindowEventServiceEvent.SET_VALUE
            | FormWindowEventServiceEvent.SET_SUBMIT
            | FormWindowEventServiceEvent.SET_CANCEL
            | FormWindowEventServiceEvent.UNSET_SUBMIT
            | FormWindowEventServiceEvent.UNSET_CANCEL
        ),
        callback : (
              FormWindowEventServiceOnSetModelCallback
            | FormWindowEventServiceOnSetValueCallback
            | FormWindowEventServiceOnSetSubmitCallback
            | FormWindowEventServiceOnSetCancelCallback
            | FormWindowEventServiceOnUnsetSubmitCallback
            | FormWindowEventServiceOnUnsetCancelCallback
        )
    ) : FormWindowEventServiceDestructor {

        if ( FormWindowEventServiceEvent.test(name) ) {

            if (!this._messageListener) {
                this._initializeMessageListener();
            }

            let destructor : any = this._observer.listenEvent(name, callback as unknown as ObserverCallback<FormWindowEventServiceEvent>);

            return () => {
                try {

                    destructor();
                    destructor = undefined;

                } finally {
                    if (
                        !this._observer.hasCallbacks(FormWindowEventServiceEvent.SET_MODEL)
                        && !this._observer.hasCallbacks(FormWindowEventServiceEvent.SET_VALUE)
                        && !this._observer.hasCallbacks(FormWindowEventServiceEvent.SET_SUBMIT)
                        && !this._observer.hasCallbacks(FormWindowEventServiceEvent.SET_CANCEL)
                        && !this._observer.hasCallbacks(FormWindowEventServiceEvent.UNSET_SUBMIT)
                        && !this._observer.hasCallbacks(FormWindowEventServiceEvent.UNSET_CANCEL)
                    ) {
                        this._unInitializeMessageListener();
                    }
                }
            };

        } else {
            throw new TypeError(`Provided event was not valid: ${name}`);
        }

    }

    public static destroy () {
        this._unInitializeMessageListener();
    }

    public static sendSubmitMessage (value: FormValue) {

        const msg : FormSubmitMessageDTO = {
            type: FormMessageType.SUBMIT,
            value: value
        };

        this._sendMessage(msg, 'http://localhost:3000');

    }

    public static sendCancelMessage () {

        const msg : FormCancelMessageDTO = {
            type: FormMessageType.CANCEL
        };

        this._sendMessage(msg, 'http://localhost:3000');

    }


    private static _sendMessage (
        message : FormSubmitMessageDTO | FormCancelMessageDTO,
        targetOrigin  : string
    ) {
        this._sendMessageString(JSON.stringify(message), targetOrigin);
    }

    private static _sendMessageString (messageString : string, targetOrigin: string) {
        if (WindowService.hasParent()) {
            LOG.debug('Sending to parent window ', messageString, targetOrigin);
            window.parent.postMessage(messageString, targetOrigin);
        } else {
            LOG.warn(`Warning! No parent window defined`);
        }
    }

    private static _initializeMessageListener () {

        if (this._messageListener) {
            this._unInitializeMessageListener();
        }

        this._messageListener = WindowEventService.on(WindowEventService.Event.JSON_MESSAGE, (eventName, data: JsonObject, origin: string) => {
            this._processMessage(data, origin);
        });

    }

    private static _unInitializeMessageListener () {

        if (this._messageListener) {
            this._messageListener();
            this._messageListener = undefined;
        }

    }

    private static _processMessage (data : JsonObject, origin: string) {

        const dataType : any | undefined = data?.type;

        if (!isFormMessageType(dataType)) return;

        switch(dataType) {

            case FormMessageType.SET_MODEL:
                if (isFormSetModelMessageDTO(data)) {
                    this._onSetModelMessage(data.model, origin);
                } else {
                    LOG.error('Provided SET_MODEL DTO was invalid: ' + JSON.stringify(data));
                }
                return;

            case FormMessageType.SET_VALUE:
                if (isFormSetValueMessageDTO(data)) {
                    this._onSetValueMessage(data.value, origin);
                } else {
                    LOG.error('Provided SET_VALUE DTO was invalid: ' + JSON.stringify(data));
                }
                return;

            case FormMessageType.SET_SUBMIT:
                if (isFormSetSubmitMessageDTO(data)) {
                    this._onSetSubmitMessage(origin);
                } else {
                    LOG.error('Provided SET_SUBMIT DTO was invalid: ' + JSON.stringify(data));
                }
                return;

            case FormMessageType.SET_CANCEL:
                if (isFormSetCancelMessageDTO(data)) {
                    this._onSetCancelMessage(origin);
                } else {
                    LOG.error('Provided SET_CANCEL DTO was invalid: ' + JSON.stringify(data));
                }
                return;

            case FormMessageType.UNSET_SUBMIT:
                if (isFormUnsetSubmitMessageDTO(data)) {
                    this._onUnsetSubmitMessage(origin);
                } else {
                    LOG.error('Provided UNSET_SUBMIT DTO was invalid: ' + JSON.stringify(data));
                }
                return;

            case FormMessageType.UNSET_CANCEL:
                if (isFormUnsetCancelMessageDTO(data)) {
                    this._onUnsetCancelMessage(origin);
                } else {
                    LOG.error('Provided UNSET_CANCEL DTO was invalid: ' + JSON.stringify(data));
                }
                return;

            default:
                return;

        }

    }

    private static _onUnsetCancelMessage (origin: string) {
        this._observer.triggerEvent(FormWindowEventServiceEvent.UNSET_CANCEL, origin);
    }

    private static _onUnsetSubmitMessage (origin: string) {
        this._observer.triggerEvent(FormWindowEventServiceEvent.UNSET_SUBMIT, origin);
    }

    private static _onSetCancelMessage (origin: string) {
        this._observer.triggerEvent(FormWindowEventServiceEvent.SET_CANCEL, origin);
    }

    private static _onSetSubmitMessage (origin: string) {
        this._observer.triggerEvent(FormWindowEventServiceEvent.SET_SUBMIT, origin);
    }

    private static _onSetModelMessage (model: FormModel, origin: string) {
        this._observer.triggerEvent(FormWindowEventServiceEvent.SET_MODEL, model, origin);
    }

    private static _onSetValueMessage (value: FormValue, origin: string) {
        this._observer.triggerEvent(FormWindowEventServiceEvent.SET_VALUE, value, origin);
    }

}

export default FormWindowEventService;
